import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  MdArrowBack, 
  MdSave, 
  MdPerson, 
  MdEuro, 
  MdCalendarToday, 
  MdDescription,
  MdAdd,
  MdDelete
} from 'react-icons/md';
import { clientesAPI, recibosAPI, formatDateForAPI, configuracionAPI } from '../services/api';

const NewReceiptContainer = styled.div`
  width: 100%;
  max-width: 100%;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const BackButton = styled.button`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.theme.colors.backgroundLight};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  color: ${props => props.theme.colors.textPrimary};
  transition: ${props => props.theme.transitions.fast};
  cursor: pointer;

  svg {
    font-size: 1.5rem;
  }

  &:hover {
    background-color: ${props => props.theme.colors.background};
    border-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.primary};
  }
`;

const Title = styled.h1`
  font-size: ${props => props.theme.fontSizes.xxxl};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
`;

const FormCard = styled.div`
  background: ${props => props.theme.colors.backgroundLight};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xl};
  box-shadow: ${props => props.theme.shadows.sm};
  border: 1px solid ${props => props.theme.colors.border};
`;

const FormSection = styled.div`
  margin-bottom: ${props => props.theme.spacing.xl};

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: ${props => props.theme.fontSizes.xl};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0 0 ${props => props.theme.spacing.sm} 0;
  font-weight: ${props => props.theme.fontWeights.semibold};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};

  svg {
    color: ${props => props.theme.colors.primary};
  }
`;

const SectionDescription = styled.p`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textSecondary};
  margin: 0 0 ${props => props.theme.spacing.lg} 0;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${props => props.theme.spacing.lg};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
`;

const Label = styled.label`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textPrimary};
  font-weight: ${props => props.theme.fontWeights.medium};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};

  span.required {
    color: ${props => props.theme.colors.danger};
  }
`;

const Input = styled.input`
  padding: ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.textPrimary};
  transition: ${props => props.theme.transitions.fast};

  &:focus {
    border-color: ${props => props.theme.colors.primary};
    background-color: ${props => props.theme.colors.backgroundLight};
    outline: none;
  }

  &::placeholder {
    color: ${props => props.theme.colors.textLight};
  }
`;

const Select = styled.select`
  padding: ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.textPrimary};
  cursor: pointer;
  transition: ${props => props.theme.transitions.fast};

  &:focus {
    border-color: ${props => props.theme.colors.primary};
    background-color: ${props => props.theme.colors.backgroundLight};
    outline: none;
  }
`;

const TextArea = styled.textarea`
  padding: ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.textPrimary};
  font-family: ${props => props.theme.fonts.primary};
  min-height: 80px;
  resize: vertical;
  transition: ${props => props.theme.transitions.fast};

  &:focus {
    border-color: ${props => props.theme.colors.primary};
    background-color: ${props => props.theme.colors.backgroundLight};
    outline: none;
  }

  &::placeholder {
    color: ${props => props.theme.colors.textLight};
  }
`;

const AutocompleteContainer = styled.div`
  position: relative;
  width: 100%;
`;

const SuggestionsDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: ${props => props.theme.colors.backgroundLight};
  border: 1px solid ${props => props.theme.colors.border};
  border-top: none;
  border-radius: 0 0 ${props => props.theme.borderRadius.md} ${props => props.theme.borderRadius.md};
  max-height: 280px;
  overflow-y: auto;
  box-shadow: ${props => props.theme.shadows.lg};
  z-index: 1000;
  margin-top: -1px;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.background};
    border-radius: ${props => props.theme.borderRadius.sm};
  }

  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.border};
    border-radius: ${props => props.theme.borderRadius.sm};
    
    &:hover {
      background: ${props => props.theme.colors.primary}50;
    }
  }
`;

const SuggestionItem = styled.div`
  padding: ${props => props.theme.spacing.md};
  cursor: pointer;
  transition: ${props => props.theme.transitions.fast};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  
  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${props => props.theme.colors.primary}15;
  }

  ${props => props.selected && `
    background-color: ${props.theme.colors.primary}20;
  `}
`;

const SuggestionName = styled.div`
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  color: ${props => props.theme.colors.textPrimary};
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const SuggestionDetails = styled.div`
  font-size: ${props => props.theme.fontSizes.xs};
  color: ${props => props.theme.colors.textSecondary};
  display: flex;
  gap: ${props => props.theme.spacing.md};
`;

const NoResults = styled.div`
  padding: ${props => props.theme.spacing.lg};
  text-align: center;
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${props => props.theme.fontSizes.sm};
`;

const ItemsSection = styled.div`
  background: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const ItemsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const ItemsTitle = styled.h3`
  font-size: ${props => props.theme.fontSizes.lg};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
  font-weight: ${props => props.theme.fontWeights.semibold};
`;

const AddItemButton = styled.button`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  background-color: ${props => props.theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  transition: ${props => props.theme.transitions.fast};
  cursor: pointer;

  svg {
    font-size: 1rem;
  }

  &:hover {
    background-color: ${props => props.theme.colors.primaryDark};
  }
`;

const ItemRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 40px;
  gap: ${props => props.theme.spacing.md};
  align-items: end;
  margin-bottom: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.md};
  background: ${props => props.theme.colors.backgroundLight};
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.theme.colors.border};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const DeleteButton = styled.button`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.theme.colors.danger}15;
  color: ${props => props.theme.colors.danger};
  border: 1px solid ${props => props.theme.colors.danger}30;
  border-radius: ${props => props.theme.borderRadius.md};
  transition: ${props => props.theme.transitions.fast};
  cursor: pointer;

  svg {
    font-size: 1.25rem;
  }

  &:hover {
    background-color: ${props => props.theme.colors.danger};
    color: white;
  }
`;

const SummaryCard = styled.div`
  background: ${props => props.theme.colors.background};
  border: 2px solid ${props => props.theme.colors.primary};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xl};
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const SummaryTitle = styled.h3`
  font-size: ${props => props.theme.fontSizes.lg};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0 0 ${props => props.theme.spacing.lg} 0;
  font-weight: ${props => props.theme.fontWeights.semibold};
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${props => props.theme.spacing.sm} 0;
  border-bottom: 1px solid ${props => props.theme.colors.border};

  &:last-child {
    border-bottom: none;
    padding-top: ${props => props.theme.spacing.md};
    margin-top: ${props => props.theme.spacing.md};
    border-top: 2px solid ${props => props.theme.colors.border};
  }
`;

const SummaryLabel = styled.span`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textSecondary};
  font-weight: ${props => props.theme.fontWeights.medium};
`;

const SummaryValue = styled.span`
  font-size: ${props => props.large ? props.theme.fontSizes.xxl : props.theme.fontSizes.md};
  color: ${props => props.theme.colors.textPrimary};
  font-weight: ${props => props.large ? props.theme.fontWeights.bold : props.theme.fontWeights.semibold};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  justify-content: flex-end;
  margin-top: ${props => props.theme.spacing.xl};
  padding-top: ${props => props.theme.spacing.xl};
  border-top: 1px solid ${props => props.theme.colors.border};
`;

const Button = styled.button`
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.xl};
  background-color: ${props => props.primary ? props.theme.colors.primary : props.theme.colors.backgroundLight};
  color: ${props => props.primary ? 'white' : props.theme.colors.textPrimary};
  border: 1px solid ${props => props.primary ? props.theme.colors.primary : props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.semibold};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  transition: ${props => props.theme.transitions.fast};
  cursor: pointer;

  svg {
    font-size: 1.25rem;
  }

  &:hover {
    background-color: ${props => props.primary ? props.theme.colors.primaryDark : props.theme.colors.background};
    transform: translateY(-1px);
    box-shadow: ${props => props.theme.shadows.sm};
  }
`;

const NewReceipt = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const today = new Date().toISOString().split('T')[0];
  
  const [formData, setFormData] = useState({
    numeroRecibo: `REC-${Date.now()}`,
    cliente: '',
    fecha: today,
    fechaVencimiento: '',
    iva: '21', // Valor por defecto, se actualizará desde configuración
    descuento: '0',
    notas: ''
  });

  const [items, setItems] = useState([
    { id: 1, descripcion: '', cantidad: 1, precioUnitario: 0, total: 0 }
  ]);

  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Estados para autocompletado
  const [clienteSearchTerm, setClienteSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedClienteId, setSelectedClienteId] = useState('');

  // Cargar configuración (IVA) al montar el componente
  useEffect(() => {
    const loadConfiguracion = async () => {
      try {
        const response = await configuracionAPI.getConfiguracion();
        const config = response.data.data;
        
        // Actualizar IVA con el valor de configuración
        if (config && config.iva !== undefined) {
          setFormData(prev => ({
            ...prev,
            iva: String(config.iva) // Convertir a string para el input
          }));
        }
      } catch (error) {
        console.error('Error al cargar configuración:', error);
        // Mantener el valor por defecto si hay error
      }
    };
    
    loadConfiguracion();
  }, []);

  // Cargar clientes al montar el componente
  useEffect(() => {
    const loadClientes = async () => {
      try {
        const response = await clientesAPI.getAll();
        const todosLosClientes = response.data.data || [];
        const clientesOrdenados = todosLosClientes.sort((a, b) => 
          a.nombre.localeCompare(b.nombre)
        );
        setClientes(clientesOrdenados);
        setLoading(false);
      } catch (error) {
        console.error('Error al cargar clientes:', error);
        window.alert('Error al cargar clientes');
        setLoading(false);
      }
    };
    
    loadClientes();
  }, []);

  // Pre-seleccionar cliente si viene desde el perfil del cliente
  useEffect(() => {
    if (location.state?.clienteId && clientes.length > 0) {
      const cliente = clientes.find(c => c.id === location.state.clienteId);
      if (cliente) {
        setSelectedClienteId(cliente.id);
        setClienteSearchTerm(cliente.nombre);
        setFormData(prev => ({
          ...prev,
          cliente: String(cliente.id) // Guardar el ID, no el nombre
        }));
      }
    }
  }, [location.state, clientes]);

  // Cerrar sugerencias al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('[data-autocomplete]')) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Funciones para autocompletado de cliente
  const handleClienteSearch = (e) => {
    const value = e.target.value;
    setClienteSearchTerm(value);
    setShowSuggestions(value.length > 0);
    
    // Si se borra todo el texto, limpiar la selección
    if (value.length === 0) {
      setSelectedClienteId('');
      setFormData(prev => ({ ...prev, cliente: '' }));
    }
  };

  const handleClienteSelect = (cliente) => {
    setClienteSearchTerm(cliente.nombre);
    setSelectedClienteId(cliente.id);
    setFormData(prev => ({ ...prev, cliente: cliente.id }));
    setShowSuggestions(false);
  };

  const getFilteredClientes = () => {
    if (!clienteSearchTerm) return [];
    
    const searchLower = clienteSearchTerm.toLowerCase();
    return clientes.filter(cliente => 
      cliente.nombre.toLowerCase().includes(searchLower) ||
      (cliente.rfc && cliente.rfc.toLowerCase().includes(searchLower)) ||
      (cliente.email && cliente.email.toLowerCase().includes(searchLower))
    ).slice(0, 8); // Limitar a 8 resultados
  };

  const handleItemChange = (id, field, value) => {
    setItems(prevItems => 
      prevItems.map(item => {
        if (item.id === id) {
          const updatedItem = { ...item, [field]: value };
          if (field === 'cantidad' || field === 'precioUnitario') {
            updatedItem.total = parseFloat(updatedItem.cantidad || 0) * parseFloat(updatedItem.precioUnitario || 0);
          }
          return updatedItem;
        }
        return item;
      })
    );
  };

  const addItem = () => {
    setItems([...items, { 
      id: Date.now(), 
      descripcion: '', 
      cantidad: 1, 
      precioUnitario: 0, 
      total: 0 
    }]);
  };

  const removeItem = (id) => {
    if (items.length > 1) {
      setItems(items.filter(item => item.id !== id));
    }
  };

  const calculateSubtotal = () => {
    return items.reduce((sum, item) => sum + (item.total || 0), 0);
  };

  const calculateIVA = () => {
    const subtotal = calculateSubtotal();
    const ivaRate = parseFloat(formData.iva || 0) / 100;
    return subtotal * ivaRate;
  };

  const calculateDiscount = () => {
    const subtotal = calculateSubtotal();
    const discountRate = parseFloat(formData.descuento || 0) / 100;
    return subtotal * discountRate;
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateIVA() - calculateDiscount();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validaciones completas
    if (!formData.cliente) {
      alert('⚠️ Por favor selecciona un cliente');
      return;
    }

    if (!formData.numeroRecibo || formData.numeroRecibo.trim() === '') {
      alert('⚠️ Por favor ingresa el número de recibo');
      return;
    }

    if (!formData.fecha) {
      alert('⚠️ Por favor selecciona la fecha de emisión');
      return;
    }

    if (!formData.fechaVencimiento) {
      alert('⚠️ Por favor selecciona la fecha de vencimiento');
      return;
    }

    if (items.length === 0 || items.every(item => !item.descripcion)) {
      alert('⚠️ Por favor agrega al menos un ítem al recibo');
      return;
    }

    // Validar que todos los ítems tengan datos válidos
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.descripcion && item.descripcion.trim() !== '') {
        if (!item.cantidad || parseFloat(item.cantidad) <= 0) {
          alert(`⚠️ El ítem "${item.descripcion}" debe tener una cantidad mayor a 0`);
          return;
        }
        if (!item.precioUnitario || parseFloat(item.precioUnitario) <= 0) {
          alert(`⚠️ El ítem "${item.descripcion}" debe tener un precio unitario mayor a 0`);
          return;
        }
      }
    }

    const total = calculateTotal();
    if (total <= 0) {
      alert('⚠️ El total del recibo debe ser mayor a 0');
      return;
    }

    setSubmitting(true);

    try {
      const total = calculateTotal();
      
      // Preparar datos del recibo para el backend
      const reciboData = {
        cliente_id: parseInt(formData.cliente),
        numero_recibo: formData.numeroRecibo,
        descripcion: items.map(item => item.descripcion).filter(d => d).join(', '),
        monto_total: total,
        saldo_pendiente: total,
        estado: 'pendiente',
        fecha_emision: formatDateForAPI(formData.fecha),
        fecha_vencimiento: formData.fechaVencimiento ? formatDateForAPI(formData.fechaVencimiento) : null,
        notas: formData.notas || null,
        items: items.filter(item => item.descripcion).map(item => ({
          descripcion: item.descripcion,
          cantidad: parseFloat(item.cantidad),
          precio_unitario: parseFloat(item.precioUnitario),
          subtotal: parseFloat(item.total)
        })),
        porcentaje_iva: parseFloat(formData.iva),
        monto_iva: calculateIVA(),
        porcentaje_descuento: parseFloat(formData.descuento),
        monto_descuento: calculateDiscount(),
        subtotal: calculateSubtotal()
      };

      const response = await recibosAPI.create(reciboData);

      if (response.data.success) {
        alert('¡Recibo creado exitosamente!');
        navigate('/recibos');
      }
    } catch (error) {
      console.error('Error al crear recibo:', error);
      alert('Error al crear recibo: ' + (error.response?.data?.message || error.message || 'Error desconocido'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <NewReceiptContainer>
      <PageHeader>
        <BackButton onClick={handleCancel}>
          <MdArrowBack />
        </BackButton>
        <Title>Nuevo Recibo</Title>
      </PageHeader>

      <FormCard>
        <form onSubmit={handleSubmit}>
          <FormSection>
            <SectionTitle>
              <MdDescription />
              Información del Recibo
            </SectionTitle>
            <SectionDescription>
              Datos básicos del recibo o factura
            </SectionDescription>
            
            <FormGrid>
              <FormGroup>
                <Label>
                  Número de Recibo <span className="required">*</span>
                </Label>
                <Input
                  type="text"
                  name="numeroRecibo"
                  value={formData.numeroRecibo}
                  onChange={handleChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>
                  <MdPerson style={{ fontSize: '1rem' }} />
                  Cliente <span className="required">*</span>
                </Label>
                <AutocompleteContainer data-autocomplete="true">
                  <Input
                    type="text"
                    placeholder={loading ? "Cargando clientes..." : "Buscar cliente por nombre, RFC o email..."}
                    value={clienteSearchTerm}
                    onChange={handleClienteSearch}
                    onFocus={() => clienteSearchTerm && setShowSuggestions(true)}
                    disabled={loading}
                    required
                  />
                  {showSuggestions && getFilteredClientes().length > 0 && (
                    <SuggestionsDropdown>
                      {getFilteredClientes().map(cliente => (
                        <SuggestionItem
                          key={cliente.id}
                          selected={selectedClienteId === cliente.id}
                          onClick={() => handleClienteSelect(cliente)}
                        >
                          <SuggestionName>{cliente.nombre}</SuggestionName>
                          <SuggestionDetails>
                            {cliente.rfc && <span>RFC: {cliente.rfc}</span>}
                            {cliente.email && <span>Email: {cliente.email}</span>}
                          </SuggestionDetails>
                        </SuggestionItem>
                      ))}
                    </SuggestionsDropdown>
                  )}
                  {showSuggestions && clienteSearchTerm && getFilteredClientes().length === 0 && (
                    <SuggestionsDropdown>
                      <NoResults>No se encontraron clientes</NoResults>
                    </SuggestionsDropdown>
                  )}
                </AutocompleteContainer>
              </FormGroup>

              <FormGroup>
                <Label>
                  <MdCalendarToday style={{ fontSize: '1rem' }} />
                  Fecha de Emisión <span className="required">*</span>
                </Label>
                <Input
                  type="date"
                  name="fecha"
                  value={formData.fecha}
                  onChange={handleChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>
                  <MdCalendarToday style={{ fontSize: '1rem' }} />
                  Fecha de Vencimiento <span className="required">*</span>
                </Label>
                <Input
                  type="date"
                  name="fechaVencimiento"
                  value={formData.fechaVencimiento}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
            </FormGrid>
          </FormSection>

          <FormSection>
            <ItemsSection>
              <ItemsHeader>
                <ItemsTitle>Conceptos / Servicios</ItemsTitle>
                <AddItemButton type="button" onClick={addItem}>
                  <MdAdd />
                  Agregar Concepto
                </AddItemButton>
              </ItemsHeader>

              {items.map((item) => (
                <ItemRow key={item.id}>
                  <FormGroup>
                    <Label>Descripción <span className="required">*</span></Label>
                    <Input
                      type="text"
                      value={item.descripcion}
                      onChange={(e) => handleItemChange(item.id, 'descripcion', e.target.value)}
                      placeholder="Ej: Servicio de consultoría"
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Cantidad</Label>
                    <Input
                      type="number"
                      value={item.cantidad}
                      onChange={(e) => handleItemChange(item.id, 'cantidad', e.target.value)}
                      min="1"
                      step="1"
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Precio Unit. ($)</Label>
                    <Input
                      type="number"
                      value={item.precioUnitario}
                      onChange={(e) => handleItemChange(item.id, 'precioUnitario', e.target.value)}
                      min="0"
                      step="0.01"
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Total ($)</Label>
                    <Input
                      type="number"
                      value={item.total.toFixed(2)}
                      readOnly
                      style={{ backgroundColor: '#f5f7fa', cursor: 'not-allowed' }}
                    />
                  </FormGroup>

                  <DeleteButton 
                    type="button" 
                    onClick={() => removeItem(item.id)}
                    disabled={items.length === 1}
                    style={{ opacity: items.length === 1 ? 0.5 : 1 }}
                  >
                    <MdDelete />
                  </DeleteButton>
                </ItemRow>
              ))}
            </ItemsSection>
          </FormSection>

          <FormSection>
            <SectionTitle>
              <MdEuro />
              Impuestos y Descuentos
            </SectionTitle>
            
            <FormGrid>
              <FormGroup>
                <Label>IVA (%)</Label>
                <Input
                  type="number"
                  name="iva"
                  value={formData.iva}
                  onChange={handleChange}
                  placeholder="21"
                  step="0.01"
                  min="0"
                  max="100"
                />
              </FormGroup>

              <FormGroup>
                <Label>Descuento (%)</Label>
                <Input
                  type="number"
                  name="descuento"
                  value={formData.descuento}
                  onChange={handleChange}
                  placeholder="0"
                  step="0.01"
                  min="0"
                  max="100"
                />
              </FormGroup>
            </FormGrid>
          </FormSection>

          <SummaryCard>
            <SummaryTitle>Resumen del Recibo</SummaryTitle>
            <SummaryRow>
              <SummaryLabel>Subtotal:</SummaryLabel>
              <SummaryValue>${calculateSubtotal().toFixed(2)}</SummaryValue>
            </SummaryRow>
            <SummaryRow>
              <SummaryLabel>IVA ({formData.iva}%):</SummaryLabel>
              <SummaryValue>${calculateIVA().toFixed(2)}</SummaryValue>
            </SummaryRow>
            <SummaryRow>
              <SummaryLabel>Descuento ({formData.descuento}%):</SummaryLabel>
              <SummaryValue>-${calculateDiscount().toFixed(2)}</SummaryValue>
            </SummaryRow>
            <SummaryRow>
              <SummaryLabel>Total:</SummaryLabel>
              <SummaryValue large>${calculateTotal().toFixed(2)}</SummaryValue>
            </SummaryRow>
          </SummaryCard>

          <FormSection>
            <FormGroup>
              <Label>Notas / Términos y Condiciones</Label>
              <TextArea
                name="notas"
                value={formData.notas}
                onChange={handleChange}
                placeholder="Información adicional, términos de pago, condiciones..."
              />
            </FormGroup>
          </FormSection>

          <ButtonGroup>
            <Button type="button" onClick={handleCancel} disabled={submitting}>
              Cancelar
            </Button>
            <Button type="submit" primary disabled={submitting}>
              <MdSave />
              {submitting ? 'Guardando...' : 'Guardar Recibo'}
            </Button>
          </ButtonGroup>
        </form>
      </FormCard>
    </NewReceiptContainer>
  );
};

export default NewReceipt;
